
import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import vSelect3 from "vselect3";
import userApi from "@/services/api.user.service";
import {
	AreaContactModalPaginatedSearchQueryParams,
	UserPermissions,
	VueBootstrapField,
	VueBootstrapFieldTableSortEvent
} from "@/store/types";
import ValidatePinCodeModal from "@/components/ValidatePinCodeModal.vue";
import { EventDetails } from "@/store/site-monitor/types";
import CustomDefinedFieldsDisplay from "@/components/custom-fields/CustomDefinedFieldsDisplay.vue";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldToggleCollapseState } from "@/store/custom-fields/types";
import { truncateString } from '@/filters';
import CustomFieldPriorityDisplay from "@/components/custom-fields/CustomFieldPriorityDisplay.vue";
import AreaContactsPagedResponse, { AreaContactsResponse } from "@/types/sv-data/contacts/AreaContactsPaginatedResponse";
import PaginatedSearch from "@/mixins/PaginatedSearch";
import { debounce } from "lodash";
import { getDebouncePeriod } from "@/utils/debounce-helper";

const SiteMonitor = namespace("siteMonitor");
const CustomFields = namespace("customFields");
const defaultMinPage: number = 1;
const defaultRole: string = "All Roles";
const customFieldsColumnKey: string = "customFields"
const pinColumnKey: string = "pin"

@Component({
	components: {
		"v-select-3": vSelect3,
		"validate-pin-code-modal": ValidatePinCodeModal,
		"custom-fields-display": CustomDefinedFieldsDisplay,
		"custom-field-priority-display": CustomFieldPriorityDisplay
	},
	filters: { truncateString }
})
export default class AreaContacts extends Mixins(PaginatedSearch) {
	@SiteMonitor.Mutation setEventDetailsShown: any;
	@SiteMonitor.Action loadAreaKeyContacts: any;
	@SiteMonitor.Getter("getEventDetails") private eventDetails: EventDetails;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getPermissions") permissions: UserPermissions;

	public areaContacts: AreaContactsResponse[] = [];
	public eventUniqueRoles: string[] = [defaultRole];
	public eventActiveRole: string = defaultRole;

	private customFieldsCollapseOpen: CustomFieldToggleCollapseState[] = [];

	private showColumnModal: boolean = false;
	private totalRecords: number = 0;
	private recordsPage: number = 1;
	private recordsPageSize: number = 25;
	private recordsPageSizeMax: string = "200";
	private recordsPageSizeUpdated: string = "25";
	private sortDesc: boolean = false;
	private sortBy: string = "title";
	private contactFilter: string = "";
	private isLoadingContacts: boolean = false;

	private readonly contactCustomFieldType = CustomFieldTableTypes.Contact;
	@Prop({ type: Number, default: false }) groupId: number;
	@Prop(String) groupTitle: string;

	@CustomFields.Action retrieveCustomFields: ({ tableType, live }) => Promise<void>;
	@CustomFields.State areaCustomFields: CustomFieldDto[];
	@CustomFields.State contactCustomFields: CustomFieldDto[];

	private contactTableColumns: VueBootstrapField[] = [];

	private async mounted(): Promise<void> {
		this.isLoadingContacts = true;

		this.contactTableColumns = [
			{
				label: 'Roles',
				key: 'roles',
				visible: true
			},
			{
				label: 'Name',
				key: 'name',
				sortable: true,
				visible: true,
			},
			{
				label: 'Phone',
				key: 'telephone',
				sortable: true,
				visible: true,
			},
			{
				label: 'Cell',
				key: 'mobile',
				sortable: true,
				visible: this.isMobileNumberEnabled,
			},
			{
				label: 'Email',
				key: 'email',
				sortable: true,
				visible: true,
			},
			{
				label: 'Pin',
				key: pinColumnKey,
				sortable: false,
				visible: true,
			},
			{
				label: 'Priority Custom Fields',
				key: customFieldsColumnKey,
				sortable: false,
				visible: true,
			},
			{
				label: 'Sort',
				key: 'sort',
				visible: true
			}
		];

		if (!this.isCustomFieldsEnabled) {
			this.contactTableColumns.removeWhere(obj => obj.key === customFieldsColumnKey);
		}

		if (!this.eventId) {
			this.contactTableColumns.removeWhere(obj => obj.key === pinColumnKey);
		}

		await this.getAreaContacts();

		if (this.areaContacts != null) {
			this.customFieldsCollapseOpen = this.areaContacts.map(item => ({
				id: item.userId,
				isOpen: false
			}));
		}

		this.isLoadingContacts = false;
	}

	private async getAreaContacts(): Promise<void> {
		const queryParams: AreaContactModalPaginatedSearchQueryParams = {
			page: this.recordsPage,
			pageSize: this.recordsPageSize,
			sortBy: this.sortBy,
			sortDesc: this.sortDesc,
			searchTerm: this.contactFilter,
			roleSearchTerm: this.eventActiveRole != defaultRole ? this.eventActiveRole : ""
		};

		this.isLoadingContacts = true;

		try {
			const result: AreaContactsPagedResponse = await userApi.getKeyContactsByGroup(this.groupId, this.generateNewPaginatedSearchRequest(queryParams));
			if(!result){
				this.areaContacts = [];
				return;
			}

			this.areaContacts = result.data;
			this.totalRecords = result.totalRecords;
		}
		catch {
			this.isLoadingContacts = false;
		}

		await this.mapEventUniqueRoles();

		this.isLoadingContacts = false;
		if (this.isCustomFieldsEnabled && this.contactCustomFields) {
			this.mapCustomFieldPriorities();
		}
	}

	private mapCustomFieldPriorities(): void {
		this.areaContacts.map(contact => {
			contact.customFieldPriorities.forEach(priority => {
				const matchingCustomField = (this.contactCustomFields).find(cf => cf.id.toString() == priority.id);
				priority.title = matchingCustomField?.title;
			});
		});
	}

	private async mapEventUniqueRoles(): Promise<any> {
		let uniqueRoles: string[] = await userApi.getContactRolesByGroup(this.groupId);
		this.eventUniqueRoles = [defaultRole, ...uniqueRoles];
	}

	private get visibleFields(): VueBootstrapField[] {
		return this.contactTableColumns.filter(c => c.visible);
	}

	private getCustomFieldCollapseState(id: number): boolean {
		let isOpen: boolean = false;

		this.customFieldsCollapseOpen.forEach(element => {
			if (element.id === id) {
				isOpen = element.isOpen;
			}
		});

		return isOpen;
	}

	@Watch("eventActiveRole")
	@Watch("eventUniqueRole")
	private resetCustomFieldCollapseState(): void {
		this.recordsPage = 1;
		this.getAreaContacts();

		this.customFieldsCollapseOpen.forEach(element => {
			element.isOpen = false;
		});
	}

	private get showContacts(): boolean {
		return this.eventUniqueRoles && this.eventUniqueRoles.length > 1;
	}

	private get isCustomFieldsEnabled(): boolean {
		return this.getFeature(["CustomFields"]);
	}

	private get isMobileNumberEnabled(): boolean {
		return this.getFeature(["Contacts", "ShowMobileNumber"]);
	}

	// Gets the Event Id from Event Details if there are any
	private get eventId(): number {
		return this.eventDetails ? this.eventDetails.eventID : null;
	}

	private async onRecordsPerPageClick(): Promise<void> {
		if (parseInt(this.recordsPageSizeUpdated) !== this.recordsPageSize) {
			this.recordsPage = 1;
		}

		this.recordsPageSize = parseInt(this.recordsPageSizeUpdated);
		await this.onPageClick(this.recordsPage);
	}

	private async onPageClick(page: number): Promise<void> {
		if (this.isLoadingContacts)
		{
			return;
		}

		this.recordsPage = page || this.recordsPage;

		await this.getAreaContacts();
	}

	private removeNonDigitsFromString(value: string): string {
		if (!value) {
			return "";
		}

		const digitFind = value.match(/\d+/g);
		if (!digitFind) {
			return "";
		}


		if (parseInt(digitFind.join("")) > parseInt(this.recordsPageSizeMax)) {
			this.recordsPageSizeUpdated = this.recordsPageSizeMax;
		}

		return digitFind.join("");
	}

	private setFilterModalOpen(): void {
		this.showColumnModal = true;
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
		await this.performSearch(searchTerm);
	}, getDebouncePeriod());

	private async performSearch(searchTerm: string): Promise<void> {
		this.contactFilter = searchTerm;
		this.recordsPage = defaultMinPage;
		await this.getAreaContacts();
	}

	private async onSortChange(event: VueBootstrapFieldTableSortEvent): Promise<void> {
		if (this.isLoadingContacts || event.sortBy == '') {
			return;
		}

		this.recordsPage = 1;
		this.sortBy = event.sortBy || this.sortBy;
		this.sortDesc = event.sortDesc;

		await this.getAreaContacts();
	}

	private get queryHasContacts(): string {
		if(!this.showContacts && !this.isLoadingContacts) {
			return 'No contacts for ' + this.groupTitle
		}

		if(this.contactFilter.length > 0 && this.areaContacts.length < 1  && !this.isLoadingContacts){
			return 'No contacts exist for search term: ' + this.contactFilter;
		}

		return null;
	}
}
